import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { writeActiveOrganizationId } from '../auth/moduleAccess';
import { formatSupabaseError } from '../lib/supabaseErrors';
import { buildDisplayName, ensureSelfOnOrgRoster } from '../db/sync/rosterSync';

/**
 * Invite acceptance page.
 * Token can be in the path (/join/:token) or query (?token=).
 * After auth, calls accept_org_invitation RPC.
 */
export default function JoinPage() {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token') || '';
  const navigate = useNavigate();
  const { session, refreshMemberships, ready, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState('loading'); // loading | accept | login | signup | set-password

  useEffect(() => {
    if (!hasSupabaseConfig()) {
      setError('Supabase is not configured.');
    }
  }, []);

  useEffect(() => {
    if (!ready || !token) return;
    if (session?.user) {
      void tryAccept();
    } else {
      setMode('login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, session?.user?.id, token]);

  const tryAccept = async (nameOverride = null) => {
    const supabase = getSupabaseClient();
    if (!supabase || !token) return;
    setBusy(true);
    setError(null);
    setMode('accept');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setError(
          'Auth session missing — you need to be signed in with the invited email before joining. Create an account or sign in below.'
        );
        setMode('login');
        return;
      }
      const userId = sessionData.session.user?.id;
      const meta = sessionData.session.user?.user_metadata || {};
      const first =
        nameOverride?.first ||
        String(meta.first_name || '').trim() ||
        firstName.trim();
      const last =
        nameOverride?.last ||
        String(meta.last_name || '').trim() ||
        lastName.trim();
      if (userId && first && last) {
        const display = buildDisplayName(first, last);
        await supabase.from('profiles').upsert({
          id: userId,
          first_name: first,
          last_name: last,
          display_name: display,
        });
      }
      const { data: orgId, error: rpcErr } = await supabase.rpc('accept_org_invitation', {
        p_token: token,
      });
      if (rpcErr) throw rpcErr;
      writeActiveOrganizationId(orgId);
      await ensureSelfOnOrgRoster(orgId, first || null, last || null);
      await refreshMemberships();
      setInfo('Invitation accepted.');
      navigate(`/orgs/${orgId}`, { replace: true });
    } catch (err) {
      const msg = formatSupabaseError(err, 'Could not accept invitation');
      setError(msg);
      if (/auth session missing|not authenticated|password|session/i.test(msg)) {
        setMode('login');
      } else if (/does not match|email/i.test(msg)) {
        setMode('wrong-user');
      } else {
        setMode('login');
      }
    } finally {
      setBusy(false);
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    const supabase = getSupabaseClient();
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (err) throw err;
      await tryAccept();
    } catch (err) {
      const msg = formatSupabaseError(err, 'Sign in failed');
      if (/email not confirmed|confirm/i.test(msg)) {
        setError(
          'Email not confirmed yet. Confirm via the email Supabase sent, or in the Supabase dashboard turn off Authentication → Providers → Email → “Confirm email” for testing.'
        );
      } else {
        setError(msg);
      }
      setMode('login');
    } finally {
      setBusy(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) {
      setError('First and last name are required');
      return;
    }
    const display = buildDisplayName(first, last);
    const supabase = getSupabaseClient();
    setBusy(true);
    setError(null);
    try {
      const trimmedEmail = email.trim();
      const { data, error: err } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/join/${token}`,
          data: {
            first_name: first,
            last_name: last,
            display_name: display,
          },
        },
      });
      if (err) throw err;

      // Prefer an immediate session; if confirm-email is on, session is null until confirmed.
      let session = data.session;
      if (!session) {
        const { data: signedIn, error: signInErr } = await supabase.auth.signInWithPassword({
          email: trimmedEmail,
          password,
        });
        if (signInErr) {
          const msg = formatSupabaseError(signInErr, 'Could not sign in after signup');
          if (/email not confirmed|confirm|session missing/i.test(msg)) {
            setInfo(
              'Account created, but email confirmation is required before you can join. Check inbox for a confirmation link, then return here and sign in — or disable “Confirm email” in Supabase Auth for testing.'
            );
            setMode('login');
            return;
          }
          throw signInErr;
        }
        session = signedIn.session;
      }

      if (!session) {
        setInfo(
          'Account created, but there is no Auth session yet (usually email confirmation). Confirm the email, then sign in with the invited address.'
        );
        setMode('login');
        return;
      }

      const userId = session.user?.id;
      if (userId) {
        await supabase.from('profiles').upsert({
          id: userId,
          first_name: first,
          last_name: last,
          display_name: display,
        });
      }

      await tryAccept({ first, last });
    } catch (err) {
      const msg = formatSupabaseError(err, 'Could not create account');
      if (/auth session missing/i.test(msg)) {
        setError(
          'Auth session missing after signup. In Supabase: Authentication → Providers → Email → turn off “Confirm email”, then try again (or confirm the email first).'
        );
        setMode('login');
      } else if (/already registered|already been registered/i.test(msg)) {
        setError('That email already has an account. Sign in instead.');
        setMode('login');
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    const supabase = getSupabaseClient();
    setBusy(true);
    setError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setError(
          'Auth session missing. Open the invite email’s magic link first, or create an account / sign in below.'
        );
        setMode('signup');
        return;
      }
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      await tryAccept();
    } catch (err) {
      setError(formatSupabaseError(err, 'Could not set password'));
    } finally {
      setBusy(false);
    }
  };

  const onSignOutAndSwitch = async () => {
    setBusy(true);
    setError(null);
    try {
      await signOut();
      setMode('login');
      setInfo('Signed out. Sign in or create an account with the invited email address.');
    } catch (err) {
      setError(formatSupabaseError(err, 'Could not sign out'));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pp-paper px-4 text-pp-ink">
        <div className="max-w-md rounded-2xl border border-pp-line bg-pp-surface p-8 text-center shadow-pp">
          <p className="text-sm text-pp-muted">Missing invitation token.</p>
          <Link to="/login" className="mt-4 inline-block text-sm font-medium text-pp-accent">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  const signedInEmail = session?.user?.email || '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-pp-paper px-4 py-12 text-pp-ink">
      <div className="w-full max-w-md rounded-2xl border border-pp-line/80 bg-pp-surface p-8 shadow-pp">
        <h1 className="text-xl font-semibold tracking-tight">Join organization</h1>
        <p className="mt-1 text-sm text-pp-muted">Accept your invitation to Pocket Parliamentarian</p>

        {info && <p className="mt-4 text-sm text-emerald-700">{info}</p>}
        {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}

        {mode === 'wrong-user' && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-pp-muted">
              You are signed in as <span className="font-medium text-pp-ink">{signedInEmail}</span>,
              which does not match the invited email. Sign out, then sign in or create an account
              with the address that received the invite.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSignOutAndSwitch()}
              className="w-full rounded-xl bg-pp-accent py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Signing out…' : 'Sign out and continue'}
            </button>
          </div>
        )}

        {mode === 'login' && (
          <form className="mt-6 space-y-4" onSubmit={onLogin}>
            <div>
              <label className="text-xs font-medium text-pp-muted">Email (must match invite)</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-pp-accent py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Joining…' : 'Sign in & join'}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setMode('signup');
              }}
              className="w-full text-xs font-medium text-pp-accent"
            >
              New here? Create an account with the invited email
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form className="mt-6 space-y-4" onSubmit={onSignup}>
            <p className="text-sm text-pp-muted">
              Use the same email the admin invited. You can join immediately if email confirmation
              is off; otherwise confirm the email first, then sign in here.
            </p>
            <div>
              <label className="text-xs font-medium text-pp-muted">Invited email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-pp-muted">First name</label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-pp-muted">Last name</label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Confirm password</label>
              <input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-pp-accent py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create account & join'}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setMode('login');
              }}
              className="w-full text-xs font-medium text-pp-accent"
            >
              Already have an account? Sign in
            </button>
          </form>
        )}

        {mode === 'set-password' && (
          <form className="mt-6 space-y-4" onSubmit={onSetPassword}>
            <p className="text-sm text-pp-muted">
              After clicking the invite email link, set a password for your account, then we will
              attach you to the organization.
            </p>
            <div>
              <label className="text-xs font-medium text-pp-muted">New password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-pp-muted">Confirm password</label>
              <input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-pp-accent py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Set password & join'}
            </button>
          </form>
        )}

        {(mode === 'accept' || mode === 'loading') && busy && (
          <p className="mt-6 text-sm text-pp-muted">Accepting invitation…</p>
        )}
      </div>
    </div>
  );
}
